<template>
  <div class="primary-color-picker">
    <div class="palette">
      <button
        v-for="color in palette"
        :key="color"
        type="button"
        class="palette-swatch"
        :class="{ 'is-active': isSwatchActive(color) }"
        :style="{ backgroundColor: color }"
        :aria-label="color"
        @click="onPickColor(color)"
      />
    </div>

    <div class="custom-hex">
      <label class="custom-hex-label">
        {{ $t('pages.settings.branding.primaryColor.customHex') }}
      </label>
      <el-input
        v-model="hexInput"
        class="custom-hex-input"
        maxlength="7"
        placeholder="#0060EE"
        @blur="onHexInputCommit"
        @keydown.enter="onHexInputCommit"
      />
      <p v-if="hexError" class="custom-hex-error">
        {{ hexError }}
      </p>
    </div>

    <div class="reset-row">
      <el-link type="primary" :underline="false" @click="onReset">
        {{ $t('pages.settings.branding.primaryColor.resetToDefault') }}
      </el-link>
    </div>

    <footer v-if="hasUnsavedPreview" class="action-bar">
      <ib-button :loading="saving" @click="onSave">
        {{ $t('save') }}
      </ib-button>
      <ib-button :disabled="saving" plain variant="white" @click="onCancel">
        {{ $t('cancel') }}
      </ib-button>
    </footer>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

const HEX_REGEX = /^#[0-9A-Fa-f]{6}$/

export default {
  name: 'PrimaryColorPicker',

  data () {
    return {
      hexInput: '',
      hexError: '',
      saving: false
    }
  },

  computed: {
    ...mapGetters('appSettings', ['effectiveColor', 'palette', 'hasUnsavedPreview', 'defaultColor'])
  },

  watch: {
    effectiveColor: {
      immediate: true,
      handler (val) {
        this.hexInput = (val || this.defaultColor).toUpperCase()
        this.hexError = ''
      }
    }
  },

  beforeDestroy () {
    if (this.hasUnsavedPreview) {
      this.cancelPreview()
    }
  },

  methods: {
    ...mapActions('appSettings', ['startPreview', 'cancelPreview', 'saveColor', 'resetToDefault']),

    isSwatchActive (color) {
      return (this.effectiveColor || '').toUpperCase() === color.toUpperCase()
    },

    onPickColor (color) {
      this.hexError = ''
      this.startPreview(color)
    },

    onHexInputCommit () {
      const value = (this.hexInput || '').trim()
      if (value === '' || value.toUpperCase() === (this.effectiveColor || '').toUpperCase()) {
        this.hexError = ''

        return
      }

      if (!HEX_REGEX.test(value)) {
        this.hexError = this.$t('pages.settings.branding.primaryColor.invalidHex')

        return
      }

      this.hexError = ''
      this.startPreview(value.toUpperCase())
    },

    async onSave () {
      this.saving = true
      try {
        await this.saveColor(this.effectiveColor)
        this.$notify({
          title: this.$t('success'),
          message: this.$t('pages.settings.branding.primaryColor.savedMessage'),
          type: 'success'
        })
      } catch (e) {
        this.$notify({
          title: this.$t('error'),
          message: this.$t('pages.settings.branding.primaryColor.saveFailed'),
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    },

    onCancel () {
      this.cancelPreview()
    },

    async onReset () {
      this.saving = true
      try {
        await this.resetToDefault()
        this.$notify({
          title: this.$t('success'),
          message: this.$t('pages.settings.branding.primaryColor.resetMessage'),
          type: 'success'
        })
      } catch (e) {
        this.$notify({
          title: this.$t('error'),
          message: this.$t('pages.settings.branding.primaryColor.saveFailed'),
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.palette {
  display: grid;
  grid-template-columns: repeat(6, 56px);
  gap: 12px;
  margin-bottom: 24px;
}

.palette-swatch {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  outline: none;
  transition: transform 120ms ease, border-color 120ms ease;

  &:hover {
    transform: scale(1.05);
  }

  &.is-active {
    border-color: #292f4d;
    box-shadow: 0 0 0 2px #fff inset;
  }
}

.custom-hex {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.custom-hex-label {
  font-weight: 500;
}

.custom-hex-input {
  max-width: 160px;
}

.custom-hex-error {
  color: #e53935;
  font-size: 13px;
  width: 100%;
  margin: 4px 0 0 0;
}

.reset-row {
  margin-top: 8px;
}

.action-bar {
  display: flex;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eef0f4;
}
</style>
